import type { ReactNode } from "react";
import { useMe, type AccessLevel, type Me, type TableAccess } from "@/api/me";

/**
 * Two kinds of access come from /api/me:
 *  - table privileges (`me.tables`): create + read/write/delete at none/own/all. Use `tableLevel` / `useTableAccess`.
 *  - permission codes (`me.permissions`): additional privileges, column security and derived
 *    `<workspace>.access` codes. Use `hasPermission` / `<Can>`.
 *
 * Permission codes look like `area.resource:action` (e.g. `finance.invoice:generate`).
 * Matching rules:
 *  - exact match
 *  - `*` grants everything
 *  - `area.*` grants every permission in an area
 *  - `area.resource:*` grants every action on a resource
 *  - asking for a wildcard (`delivery.*`) is satisfied by any permission in that area
 */
export function hasPermission(granted: readonly string[] | undefined, code: string): boolean {
  if (!granted || granted.length === 0) return false;
  if (granted.includes("*") || granted.includes(code)) return true;

  if (code.endsWith(".*")) {
    const area = code.slice(0, -1); // "delivery."
    return granted.some((p) => p.startsWith(area));
  }

  const [resourcePart] = code.split(":");
  const area = code.split(".")[0];
  return granted.some(
    (p) => (area !== undefined && p === `${area}.*`) || (resourcePart !== undefined && p === `${resourcePart}:*`),
  );
}

export function hasAnyPermission(granted: readonly string[] | undefined, codes: readonly string[]): boolean {
  return codes.some((c) => hasPermission(granted, c));
}

/** True when the signed-in user holds the permission. False while /me is loading. */
export function usePermission(code: string): boolean {
  const { data } = useMe();
  return hasPermission(data?.permissions, code);
}

export function usePermissions(): { me: Me | undefined; can: (code: string) => boolean } {
  const { data } = useMe();
  return { me: data, can: (code) => hasPermission(data?.permissions, code) };
}

export type TablePrivilege = "read" | "write" | "delete";

/** The user's level for one privilege on a table ("none" when the table isn't readable or /me hasn't loaded). */
export function tableLevel(me: Pick<Me, "tables"> | undefined, entity: string, privilege: TablePrivilege): AccessLevel {
  return me?.tables?.[entity]?.[privilege] ?? "none";
}

/** Effective table privileges for the signed-in user; all "none" while /me is loading. */
export function useTableAccess(entity: string): TableAccess {
  const { data } = useMe();
  return data?.tables?.[entity] ?? NO_TABLE_ACCESS;
}

const NO_TABLE_ACCESS: TableAccess = { create: false, read: "none", write: "none", delete: "none" };

interface CanProps {
  /** One permission, or several (any-of by default). */
  permission: string | readonly string[];
  /** Require every listed permission rather than any. */
  all?: boolean;
  fallback?: ReactNode;
  children: ReactNode;
}

/** Render children only when the user has the permission(s). */
export function Can({ permission, all = false, fallback = null, children }: CanProps) {
  const { data } = useMe();
  const codes = typeof permission === "string" ? [permission] : permission;
  const allowed = all
    ? codes.every((c) => hasPermission(data?.permissions, c))
    : hasAnyPermission(data?.permissions, codes);
  return <>{allowed ? children : fallback}</>;
}
